import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuthStore } from '@/lib/store';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const AnalyticsDashboard = ({ eventId }) => {
  const [analytics, setAnalytics] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [checkinData, setCheckinData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const { token } = useAuthStore();

  useEffect(() => {
    fetchAllAnalytics();
  }, [eventId, token]);

  const fetchAllAnalytics = async () => {
    setLoading(true);
    // Get token from auth store or fallback to localStorage for backward compatibility
    const authToken = token || getToken();
    if (!authToken) {
      console.error('No auth token available');
      setLoading(false);
      return;
    }
    const headers = { 'Authorization': `Bearer ${authToken}` };

    try {
      const [analyticsRes, revenueRes, checkinRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/events/${eventId}/analytics`, { headers }),
        fetch(`${API_URL}/api/admin/events/${eventId}/analytics/revenue`, { headers }),
        fetch(`${API_URL}/api/admin/events/${eventId}/analytics/checkin`, { headers })
      ]);

      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      if (revenueRes.ok) setRevenue(await revenueRes.json());
      if (checkinRes.ok) setCheckinData(await checkinRes.json());
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const exportData = async () => {
    try {
      const authToken = token || getToken();
      const response = await fetch(`${API_URL}/api/admin/events/${eventId}/analytics/export`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `delegates_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin text-4xl mb-4">📊</div>
          <p className="text-gray-500">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Event Analytics</h2>
          <p className="text-gray-500">{analytics?.event_name}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchAllAnalytics}>
            🔄 Refresh
          </Button>
          <Button onClick={exportData}>
            📥 Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-3xl font-bold text-blue-600">
                {analytics.registrations?.total || analytics.summary?.total_registrations || 0}
              </div>
              <p className="text-sm text-gray-500">Total Registrations</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-3xl font-bold text-green-600">
                {analytics.checkins?.total || analytics.summary?.checked_in || 0}
              </div>
              <p className="text-sm text-gray-500">Checked In ({Math.round(analytics.checkins?.rate || analytics.summary?.checkin_rate || 0)}%)</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-3xl font-bold text-amber-600">
                {analytics.registrations?.pending || analytics.summary?.pending_payment || 0}
              </div>
              <p className="text-sm text-gray-500">Pending</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-3xl font-bold text-purple-600">
                ${(analytics.economy?.total_circulation || analytics.summary?.total_revenue || 0).toLocaleString()}
              </div>
              <p className="text-sm text-gray-500">Economy Circulation</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="registrations">Registrations</TabsTrigger>
          <TabsTrigger value="checkins">Check-ins</TabsTrigger>
          <TabsTrigger value="revenue">Revenue</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Ticket Tiers Pie Chart */}
            {(analytics?.tier_breakdown?.length > 0 || analytics?.tiers?.length > 0) && (
              <Card>
                <CardHeader>
                  <CardTitle>Registrations by Tier</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analytics.tier_breakdown || analytics.tiers}
                        dataKey={analytics.tier_breakdown ? "sold" : "count"}
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={({ name, sold, count }) => `${name}: ${sold || count}`}
                      >
                        {(analytics.tier_breakdown || analytics.tiers).map((entry, index) => (
                          <Cell key={index} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Company Breakdown */}
            {analytics?.company_breakdown?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Top Companies</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analytics.company_breakdown} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="company" type="category" width={120} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="delegates" fill="#3B82F6" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Tier Details Table */}
          {(analytics?.tier_breakdown?.length > 0 || analytics?.tiers?.length > 0) && (
            <Card>
              <CardHeader>
                <CardTitle>Ticket Tier Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3">Tier</th>
                        <th className="text-right py-2 px-3">Count</th>
                        <th className="text-right py-2 px-3">Capacity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(analytics.tier_breakdown || analytics.tiers).map((tier, index) => (
                        <tr key={index} className="border-b">
                          <td className="py-2 px-3 font-medium">{tier.name}</td>
                          <td className="py-2 px-3 text-right">{tier.sold || tier.count || 0}</td>
                          <td className="py-2 px-3 text-right">{tier.capacity || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Registrations Tab */}
        <TabsContent value="registrations">
          {analytics?.registration_timeline?.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Registration Timeline (Last 30 Days)</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={analytics.registration_timeline}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="registrations" 
                      stroke="#3B82F6" 
                      strokeWidth={2}
                      dot={{ fill: '#3B82F6' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                No registration data available for the last 30 days
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Check-ins Tab */}
        <TabsContent value="checkins" className="space-y-6">
          {checkinData && (
            <>
              <div className="grid md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6 text-center">
                    <div className="text-4xl font-bold text-green-600">{checkinData.checked_in}</div>
                    <p className="text-gray-500">Checked In</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <div className="text-4xl font-bold text-amber-600">{checkinData.not_checked_in}</div>
                    <p className="text-gray-500">Pending</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <div className="text-4xl font-bold text-blue-600">{checkinData.checkin_rate}%</div>
                    <p className="text-gray-500">Check-in Rate</p>
                  </CardContent>
                </Card>
              </div>

              {checkinData.hourly_checkins?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Today's Check-ins by Hour</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={checkinData.hourly_checkins}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="hour" tickFormatter={(h) => `${h}:00`} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#10B981" name="Check-ins" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {checkinData.recent_checkins?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Recent Check-ins</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {checkinData.recent_checkins.map((checkin, index) => (
                        <div key={index} className="flex justify-between items-center py-2 border-b last:border-0">
                          <div>
                            <div className="font-medium">{checkin.name}</div>
                            {checkin.company && <div className="text-sm text-gray-500">{checkin.company}</div>}
                          </div>
                          <div className="text-sm text-gray-500">
                            {new Date(checkin.checked_in_at).toLocaleTimeString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Revenue Tab */}
        <TabsContent value="revenue" className="space-y-6">
          {revenue && (
            <>
              <div className="grid md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6 text-center">
                    <div className="text-4xl font-bold text-green-600">${revenue.total_revenue?.toLocaleString() || 0}</div>
                    <p className="text-gray-500">Total Revenue</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <div className="text-4xl font-bold text-red-600">${revenue.total_refunds?.toLocaleString() || 0}</div>
                    <p className="text-gray-500">Refunds</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <div className="text-4xl font-bold text-blue-600">${revenue.net_revenue?.toLocaleString() || 0}</div>
                    <p className="text-gray-500">Net Revenue</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {revenue.by_payment_method?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Revenue by Payment Method</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie
                            data={revenue.by_payment_method}
                            dataKey="total"
                            nameKey="method"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label
                          >
                            {revenue.by_payment_method.map((entry, index) => (
                              <Cell key={index} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => `$${value}`} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {revenue.by_tier?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Revenue by Tier</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={revenue.by_tier}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="tier" />
                          <YAxis tickFormatter={(v) => `$${v}`} />
                          <Tooltip formatter={(value) => `$${value}`} />
                          <Bar dataKey="total" fill="#8B5CF6" name="Revenue" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </div>

              {revenue.daily_revenue?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Daily Revenue (Last 30 Days)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={revenue.daily_revenue}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                        <YAxis tickFormatter={(v) => `$${v}`} />
                        <Tooltip formatter={(value) => `$${value}`} />
                        <Line 
                          type="monotone" 
                          dataKey="revenue" 
                          stroke="#10B981" 
                          strokeWidth={2}
                          dot={{ fill: '#10B981' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AnalyticsDashboard;
